import type { MetaArtifact } from '@ev/shared'
import { useEffect } from 'react'
import {
  type AvailabilityIndex,
  loadAvailabilityIndex,
  loadMeta,
  loadStationIndex,
  type StationIndex,
} from './cache'
import { useDataHealthStore } from './health'
import { type Resource, useResource } from './resource'

/**
 * React bindings over the artifact cache.
 *
 * These are thin on purpose. The cache decides what is fetched and when; these
 * decide what a component sees while it happens. Because the cache dedupes,
 * three components calling `useStationIndex()` is still one network request -
 * which is how the map, the search box and the keyboard list share one copy of
 * the national feature set instead of pulling it three times.
 */

/** Stable keys, so the resource effect does not refire on every render. */
const STATIONS_KEY = 'stations'
const AVAILABILITY_KEY = 'availability'
const META_KEY = 'meta'

const readStations = (): Promise<StationIndex> => loadStationIndex()
const readAvailability = (): Promise<AvailabilityIndex> => loadAvailabilityIndex()
const readMeta = (): Promise<MetaArtifact> => loadMeta()

export const useStationIndex = (): {
  readonly resource: Resource<StationIndex>
  readonly reload: () => void
} => useResource(STATIONS_KEY, readStations)

/**
 * The availability index, fetched the first time it is asked for.
 *
 * `status.json` is the largest artifact of the set and only the availability
 * facet needs it, so nothing pays for it until that facet is ticked. A failure
 * here is a partial outage, not a fatal one: the map keeps drawing every site
 * and the notice says live availability is missing, rather than the facet
 * silently filtering everything away.
 */
export const useAvailabilityIndex = (enabled: boolean): AvailabilityIndex | undefined => {
  const { resource } = useResource(enabled ? AVAILABILITY_KEY : null, readAvailability)
  const markUnavailable = useDataHealthStore((state) => state.markUnavailable)
  const markAvailable = useDataHealthStore((state) => state.markAvailable)

  useEffect(() => {
    if (resource.status === 'error') markUnavailable('status')
    if (resource.status === 'ready') markAvailable('status')
  }, [resource.status, markUnavailable, markAvailable])

  return resource.status === 'ready' ? resource.value : undefined
}

/**
 * `meta.json`, which drives the freshness line in the footer, the stale notice
 * and the about page's quality tables - all from one read.
 */
export const useMeta = (): {
  readonly resource: Resource<MetaArtifact>
  readonly reload: () => void
} => useResource(META_KEY, readMeta)
